import React, { useState } from 'react';
import { Confirm, useNotify } from 'react-admin';
import { read_cookie } from 'sfcookies';
import {
    IconButton,
    CircularProgress,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
} from '@mui/material';
// Not a circular-arrow icon: react-admin's own refresh button sits two places along in this
// header, and two near-identical arrows would read as the same control.
import LayersClearIcon from '@mui/icons-material/LayersClear';
import PublicIcon from '@mui/icons-material/Public';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PolicyIcon from '@mui/icons-material/Policy';
import { hostUrl, UseUrlManagerCleanUrl } from '../utils/url-manager';

/**
 * One flushable cache.
 *
 * **Adding a cache is meant to be one entry here plus its endpoint in HQ-API.** Each carries its
 * own endpoint rather than sharing one with a parameter, because what "clear" means differs per
 * cache and each is likely to want its own guard.
 */
interface FlushableCache {
    id: string;
    label: string;
    /** Shown under the label — say what a visitor sees change, not what the pool is called. */
    description: string;
    endpoint: string;
    icon: React.JSX.Element;
}

const CACHES: FlushableCache[] = [
    {
        id: 'feature',
        label: 'Feature pages',
        description: 'Public landing content for each Nok Mini',
        endpoint: 'hq/cache/feature/flush',
        icon: <PublicIcon fontSize="small" />,
    },
    {
        id: 'mystery-shopper-results',
        label: 'Mystery Shopper results',
        description: 'Portal results screens for complete shopping runs',
        endpoint: 'hq/cache/mystery-shopper-results/flush',
        icon: <StorefrontIcon fontSize="small" />,
    },
    {
        id: 'compliance-sniffer-results',
        label: 'Compliance Sniffer results',
        description: 'Portal results screens for complete sniffing runs',
        endpoint: 'hq/cache/compliance-sniffer-results/flush',
        icon: <PolicyIcon fontSize="small" />,
    },
];

interface FlushResponse {
    status?: string;
    message?: string;
}

/**
 * The header's cache menu.
 *
 * Feature content is cached for an hour, so an edit made in HQ does not reach the public site until
 * the entry expires. This is how to make it show now.
 */
export const CacheFlushMenu = (): React.JSX.Element => {
    const notify = useNotify();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [flushing, setFlushing] = useState<string | null>(null);
    const [pending, setPending] = useState<FlushableCache | null>(null);

    const open: boolean = Boolean(anchorEl);

    async function flushCache(cache: FlushableCache): Promise<void> {
        setFlushing(cache.id);
        setPending(null);
        let url: string = hostUrl + cache.endpoint;
        url = UseUrlManagerCleanUrl({ url: url });
        const token: string = read_cookie('auth');
        const request: Request = new Request(url, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'token': token,
            }),
        });

        return fetch(request)
            .then((response: Response) => response.json())
            .then((data: FlushResponse) => {
                // The API answers 503 with an error status when it could not reach the cache. A
                // flush that did not happen must not read as one: the admin pressed this because a
                // public page is stale, so a false success sends them looking in the wrong place.
                if (data.status === 'success') {
                    notify(cache.label + ' cache flushed', { type: 'success' });
                } else {
                    notify(data.message ?? 'Unable to flush the ' + cache.label + ' cache', { type: 'error' });
                }
                setFlushing(null);
            })
            .catch((error: Error) => {
                console.log(error);
                notify('Unable to flush the ' + cache.label + ' cache', { type: 'error' });
                setFlushing(null);
            });
    }

    return (
        <>
            <Tooltip title="Flush caches">
                <span>
                    <IconButton
                        color="inherit"
                        disabled={ flushing !== null }
                        onClick={ (event: React.MouseEvent<HTMLElement>): void => setAnchorEl(event.currentTarget) }
                        aria-label="Flush caches"
                        aria-haspopup="true"
                        aria-expanded={ open }
                    >
                        { flushing !== null
                            ? <CircularProgress size={ 20 } color="inherit" />
                            : <LayersClearIcon /> }
                    </IconButton>
                </span>
            </Tooltip>

            <Menu
                anchorEl={ anchorEl }
                open={ open }
                onClose={ (): void => setAnchorEl(null) }
                anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
                transformOrigin={ { vertical: 'top', horizontal: 'right' } }
                slotProps={ { list: { dense: true, sx: { minWidth: 300, pr: 1 } } } }
            >
                <ListSubheader sx={ { lineHeight: '32px' } }>{ 'Flush a cache' }</ListSubheader>
                { CACHES.map((cache: FlushableCache) => (
                    <MenuItem
                        key={ cache.id }
                        onClick={ (): void => {
                            setAnchorEl(null);
                            setPending(cache);
                        } }
                    >
                        <ListItemIcon>{ cache.icon }</ListItemIcon>
                        <ListItemText primary={ cache.label } secondary={ cache.description } />
                    </MenuItem>
                )) }
            </Menu>

            <Confirm
                isOpen={ pending !== null }
                title={ 'Flush the ' + (pending?.label ?? '') + ' cache' }
                content={ 'It will be rebuilt from the database on the next request. No other cache is affected.' }
                onConfirm={ (): void => { if (pending !== null) { void flushCache(pending); } } }
                onClose={ (): void => setPending(null) }
            />
        </>
    );
};
